import React, { useEffect } from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import Script from 'react-load-script';

import LoadingSpinner from '../loadingIndicator/CircularSpinner';
import InputLabel from '../inputfields/InputLabel';
import COLORS from '../../constants/colors';

import { isDefined, toReadableCoordinate } from '../../utils/helpers';
import TextField from '@material-ui/core/TextField';
import useFieldValidation from 'hooks/validation/useFieldValidation';

export const extractAddress = async address => {
  let street = '';
  let streetNumber = '';
  let city = '';
  let postalCode = '';
  let latLng = {};

  try {
    const results = await geocodeByAddress(address);
    const firstResult = results[0];
    latLng = await getLatLng(firstResult);

    const components = firstResult.address_components;

    street = components.find(c => c.types.includes('route'))?.long_name || '';
    streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
    city =
      components.find(c => c.types.includes('locality'))?.long_name ||
      components.find(c => c.types.includes('postal_town'))?.long_name ||
      components.find(c => c.types.includes('administrative_area_level_2'))?.long_name ||
      components.find(c => c.types.includes('administrative_area_level_1'))?.long_name ||
      '';
    postalCode = components.find(c => c.types.includes('postal_code'))?.long_name || '';
  } catch (error) {
    console.log(`Could not fetch coordinates for address: ${address}`);
  }

  if (!street && address) {
    const match = address.match(/(.+)\s(\d+)$/);
    if (match) {
      street = match[1];
      streetNumber = match[2];
    } else {
      street = address;
    }
  }

  return { street, streetNumber, city, postalCode, latLng };
};

const Autocomplete = ({
  readOnly = false,
  validatorMode = '',
  validator = undefined,
  displayStreetOnly = false,
  countryIso2 = null,
  required = false,
  inputName = '',
  label = ' Sök adress...',
  containerStyle = {},
  dropDownStyle = {},
  onFailureLoadingComponent = () => {},
  onSelectAddress,
  withLabel,
  labelOptions,
  currentAddress,
  showLoadingIndicator = true,
  showLatLngInfo = false,
  latitude = 0,
  longitude = 0,
  onBlur = () => {},
}) => {
  const getAddress = () => (isDefined(currentAddress) ? currentAddress : '');
  const [address, setAddress] = React.useState(getAddress());
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const handleChange = address => {
    setAddress(address);
  };

  const validation = useFieldValidation(inputName, {
    mode: validatorMode,
    value: address,
    required,
    address,
    validator,
    onBlur,
  });

  useEffect(() => {
    setAddress(getAddress());
  }, [currentAddress]);

  const handleSelect = address => {
    if (!displayStreetOnly) {
      setAddress(address);
    }

    // Get coordinates.
    extractAddress(address).then(({ street, streetNumber, city, postalCode, latLng }) => {
      const fullStreet = `${street || ''} ${streetNumber || ''}`.trim();
      if (displayStreetOnly) {
        setAddress(fullStreet);
      }
      onSelectAddress(address, latLng, { fullStreet, street, streetNumber, city, postalCode });
    });
  };

  const onScriptBeginLoad = () => {
    setScriptLoaded(false);
  };

  const onScriptLoadedSuccess = () => {
    setScriptLoaded(true);
  };

  const onScriptLoadedError = () => {
    setScriptLoaded(true);
    onFailureLoadingComponent();
  };

  const onMapsAPIError = (_, clearSuggestions) => {
    clearSuggestions();
  };

  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

  return (
    <div style={{ position: 'relative', width: '100%', marginTop: 7, ...containerStyle }}>
      {withLabel && <InputLabel {...labelOptions} />}
      <Script
        url={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`}
        onError={onScriptLoadedError}
        onLoad={onScriptLoadedSuccess}
        onCreate={onScriptBeginLoad}
      />
      {!scriptLoaded && <span>Loading address search...</span>}
      {scriptLoaded && (
        <>
          <PlacesAutocomplete
            value={address}
            onChange={!readOnly ? handleChange : undefined}
            onSelect={!readOnly ? handleSelect : undefined}
            onError={onMapsAPIError}
            searchOptions={{
              componentRestrictions: { country: countryIso2 },
            }}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <div>
                <TextField
                  disabled={readOnly}
                  style={{ marginTop: '5px' }}
                  fullWidth
                  name={inputName}
                  error={!!validation.error}
                  label={label}
                  variant="outlined"
                  required={required}
                  inputProps={{
                    ...getInputProps({
                      className: 'location-search-input',
                      style: { width: '100%' },
                      disabled: readOnly,
                      tabIndex: readOnly ? -1 : undefined,
                    }),
                    'aria-invalid': !!validation.error,
                    'aria-describedby': validation.error,
                  }}
                  InputProps={{
                    endAdornment: loading && showLoadingIndicator && <LoadingSpinner size={20} />,
                  }}
                  helperText={validation.error}
                  onBlur={!readOnly ? validation.onBlur : undefined}
                />
                <div
                  className="autocomplete-dropdown-container"
                  style={
                    isDefined(suggestions) && suggestions.length > 0
                      ? {
                          position: 'absolute',
                          width: '100%',
                          zIndex: 1000,
                          padding: 4,
                          fontSize: 16,
                          backgroundColor: 'white',
                          borderRadius: 5,
                          borderStyle: 'solid',
                          borderWidth: 2,
                          borderColor: 'gray',
                          ...dropDownStyle,
                        }
                      : { height: 0 }
                  }
                >
                  {suggestions.map(suggestion => {
                    const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item';
                    const style = suggestion.active
                      ? {
                          backgroundColor: COLORS.mainGreen,
                          borderRadius: 5,
                          cursor: 'pointer',
                          opacity: 1,
                          padding: 2,
                          fontWeight: 'bold',
                        }
                      : {
                          backgroundColor: '#ffffff',
                          cursor: 'pointer',
                          opacity: 1,
                        };
                    return (
                      <div
                        {...getSuggestionItemProps(suggestion, {
                          className,
                          style,
                        })}
                      >
                        <span>{suggestion.description}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </PlacesAutocomplete>
          {showLatLngInfo && (
            <p style={{ marginTop: 2, marginLeft: 2 }}>
              Coordinates: {toReadableCoordinate(latitude)}, {toReadableCoordinate(longitude)}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default Autocomplete;
